import React from 'react'
import { Link } from 'react-router-dom'
import {Train, MapPin, Calendar, Info} from 'lucide-react'

function Logistics() {
  const guides = [
    {
      id: 1,
      title: 'Narita Airport to Tokyo',
      description: 'Compare all transfer options: NEX, Keisei Skyliner, buses, and taxis. Find the best choice for your budget and schedule.',
      icon: '✈️',
      estimatedTime: '60-90 mins',
      priceRange: '¥1,000 - ¥3,000',
      link: '/article/narita-transfer',
      difficulty: 'Easy'
    },
    {
      id: 2,
      title: 'JR Pass: Is It Worth It?',
      description: 'Complete cost-benefit analysis of the Japan Rail Pass. Calculate if it saves you money based on your itinerary.',
      icon: '🚄',
      estimatedTime: 'N/A',
      priceRange: '¥29,650 (7 days)',
      link: '/article/jr-pass-guide',
      difficulty: 'Medium'
    },
    {
      id: 3,
      title: 'Hakone Day Trip',
      description: 'Perfect one-day itinerary for Mt. Fuji views, hot springs, and the Hakone Loop. Includes transport options and timing.',
      icon: '🗻',
      estimatedTime: 'Full Day',
      priceRange: '¥4,000 - ¥8,000',
      link: '/article/hakone-day-trip',
      difficulty: 'Easy'
    },
    {
      id: 4,
      title: 'Tokyo Subway Master Guide',
      description: 'Navigate Tokyo\'s subway like a local. Best passes, apps, etiquette, and insider tips.',
      icon: '🚇',
      estimatedTime: 'N/A',
      priceRange: '¥600 - ¥1,000/day',
      link: '/article/subway-guide',
      difficulty: 'Easy'
    },
    {
      id: 5,
      title: 'Haneda Airport to Tokyo',
      description: 'Fast and convenient transfer options from Haneda. Includes late-night options and luggage services.',
      icon: '🛫',
      estimatedTime: '30-45 mins',
      priceRange: '¥500 - ¥2,000',
      link: '/article/haneda-transfer',
      difficulty: 'Easy'
    },
    {
      id: 6,
      title: 'Nikko Day Trip',
      description: 'UNESCO temples, waterfalls, and nature. Complete transport guide and suggested routes.',
      icon: '⛩️',
      estimatedTime: 'Full Day',
      priceRange: '¥5,000 - ¥10,000',
      link: '/article/nikko-day-trip',
      difficulty: 'Medium'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-white">
      {/* Header - Clean White Style */}
      <section className="relative py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Train className="w-10 h-10 text-gray-900" />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Logistics & <span className="text-blue-600">Transport</span>
            </h1>
          </div>
          <p className="text-xl text-gray-700 max-w-3xl">
            Practical, no-nonsense guides to getting around Tokyo and beyond. 
            Real prices, accurate timing, and insider tips to save time and money.
          </p>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <section className="bg-blue-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold mb-1">60+ mins</div>
              <div className="text-blue-100">Average Airport Transfer</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">¥600-1,000</div>
              <div className="text-blue-100">Daily Subway Pass</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-1">99.9%</div>
              <div className="text-blue-100">Train On-Time Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {guides.map((guide) => (
            <div
              key={guide.id}
              className="group bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all p-6"
            >
              {/* Icon */}
              <div className="text-5xl mb-4">{guide.icon}</div>

              {/* Difficulty Badge */}
              <div className="inline-block mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  guide.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {guide.difficulty}
                </span>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                {guide.title}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {guide.description}
              </p>

              {/* Info Grid */}
              <div className="space-y-2 mb-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4" />
                  <span>{guide.estimatedTime}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Info className="w-4 h-4" />
                  <span>{guide.priceRange}</span>
                </div>
              </div>

              {/* CTA Button */}
              <Link
                to={guide.link}
                className="block w-full py-3 bg-gray-900 text-white font-bold rounded-lg text-center hover:bg-blue-600 transition-colors"
              >
                Read Full Guide →
              </Link>
            </div>
          ))}
        </div>

        {/* JR Pass CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-12 text-center text-white">
          <Train className="w-16 h-16 mx-auto mb-6" />
          <h3 className="text-3xl font-bold mb-4">
            Planning Multiple Day Trips?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            The JR Pass might save you hundreds. Use our calculator to find out if it's worth it for your trip.
          </p>
          <Link
            to="/article/jr-pass-guide"
            className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full hover:bg-gray-100 transition-colors"
          >
            Book JR Pass Now →
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Logistics
